import { useCallback, useEffect, useRef, useState } from 'react';
import request from '~/utils/request';

// Backoff parameters
const BASE_DELAY_MS = 5_000;   // first retry: 5 s
const MAX_DELAY_MS  = 60_000;  // cap at 60 s
const JITTER_RATIO  = 0.2;     // ±20 % randomness — spreads concurrent users

/**
 * Maximum number of automatic poll attempts before giving up and showing
 * the manual "Recheck" button.
 *
 * Timeline with this backoff schedule (approximate):
 *   attempt 0 → immediate
 *   attempt 1 → ~5 s   (cumulative: ~5 s)
 *   attempt 2 → ~10 s  (~15 s)
 *   attempt 3 → ~20 s  (~35 s)
 *   attempt 4 → ~40 s  (~75 s)
 *   attempt 5 → ~60 s  (~135 s)
 *   attempt 6 → ~60 s  (~195 s)
 *   attempt 7 → ~60 s  (~255 s)
 *   attempt 8 → ~60 s  (~315 s ≈ 5 min 15 s)
 *   attempt 9 → stop → show Recheck button
 *
 * 9 attempts covers ~5 minutes of automatic monitoring without any
 * manual intervention. After that the user is shown a "Recheck" button
 * so they always have a way to trigger a check on demand.
 */
const MAX_ATTEMPTS = 9;

/**
 * Exponential backoff with jitter.
 * attempt 0 → ~5 s, 1 → ~10 s, 2 → ~20 s, 3 → ~40 s, 4+ → ~60 s
 */
const URGENT_BASE_DELAY_MS = 2_000;
const URGENT_MAX_DELAY_MS = 8_000;

const getDelay = (attempt, urgent = false) => {
  const base = urgent ? URGENT_BASE_DELAY_MS : BASE_DELAY_MS;
  const max = urgent ? URGENT_MAX_DELAY_MS : MAX_DELAY_MS;
  const exp = Math.min(base * 2 ** attempt, max);
  const jitter = exp * JITTER_RATIO * (Math.random() * 2 - 1);
  return Math.round(exp + jitter);
};

/**
 * usePaymentStatusPoller
 *
 * Polls POST /auth/me while `isActive` is true, using exponential backoff,
 * a hard attempt cap, and the Page Visibility API to avoid server overload.
 *
 * Returns:
 *   checking  — true while a request is in flight (drives the live dot)
 *   exhausted — true once MAX_ATTEMPTS have fired with no resolution
 *   reset     — call this to restart polling (e.g. from a "Recheck" button)
 */
const usePaymentStatusPoller = (isActive, urgent = false) => {
  const [checking,  setChecking]  = useState(false);
  const [exhausted, setExhausted] = useState(false);

  // Mutable ref bundle — closures inside setTimeout always read current values
  // without needing to re-create the effect or the poll function.
  const state = useRef({
    active:    false,
    urgent:    false,
    attempt:   0,
    timeoutId: null,
    poll:      null,  // filled in by the effect; used by reset()
  });

  useEffect(() => {
    const s = state.current;

    if (!isActive) {
      s.active = false;
      s.urgent = false;
      s.attempt = 0;
      if (s.timeoutId) clearTimeout(s.timeoutId);
      setChecking(false);
      setExhausted(false);
      return undefined;
    }

    const wasActive = s.active;
    const wasUrgent = s.urgent;
    s.active = true;
    s.urgent = urgent;

    if (!wasActive) {
      s.attempt = 0;
      setExhausted(false);
    } else if (wasUrgent !== urgent) {
      s.attempt = 0;
      if (s.timeoutId) clearTimeout(s.timeoutId);
    }

    const poll = () => {
      if (!s.active) return;

      // Hard stop — show the manual Recheck button instead.
      if (s.attempt >= MAX_ATTEMPTS) {
        setChecking(false);
        setExhausted(true);
        return;
      }

      // Tab is hidden — skip this tick but keep the backoff counter intact so
      // we don't pretend progress was made while the user was away.
      if (document.hidden) {
        s.timeoutId = setTimeout(poll, getDelay(s.attempt, s.urgent));
        return;
      }

      setChecking(true);
      request
        .post('/auth/me', {})
        .catch(() => {})
        .finally(() => {
          if (!s.active) return;
          setChecking(false);
          s.attempt += 1;
          s.timeoutId = setTimeout(poll, getDelay(s.attempt, s.urgent));
        });
    };

    s.poll = poll;

    // Returning to a visible tab resets the backoff and fires immediately —
    // the user re-engaging is a strong signal they want a fast answer.
    const onVisibilityChange = () => {
      if (document.hidden || !s.active) return;
      if (s.timeoutId) clearTimeout(s.timeoutId);
      s.attempt = 0;
      setExhausted(false);
      poll();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    poll();

    return () => {
      s.active = false;
      if (s.timeoutId) clearTimeout(s.timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isActive, urgent]);

  // Restart polling from scratch — called when the user clicks "Recheck".
  // Safe to call at any time; no-ops when the poller is inactive.
  const reset = useCallback(() => {
    const s = state.current;
    if (!s.active || !s.poll) return;
    if (s.timeoutId) clearTimeout(s.timeoutId);
    s.attempt = 0;
    setExhausted(false);
    s.poll();
  }, []);

  return { checking, exhausted, reset };
};

export default usePaymentStatusPoller;
